// src/config/firebase.ts
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  AppCheck,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { getAuth, GoogleAuthProvider, Auth } from 'firebase/auth';
import { getFirestore, Firestore } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getDatabase, Database } from 'firebase/database';
import { getFunctions, Functions } from 'firebase/functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY?.trim();

// In local development, use an App Check debug token so requests from
// localhost (where reCAPTCHA Enterprise can't attest) still pass App Check —
// e.g. Firebase AI Logic / Firestore calls. Must be set BEFORE
// initializeAppCheck. On first run the SDK prints a token to the console;
// register it under Firebase Console → App Check → Apps → Manage debug tokens
// (or pin a stable one via VITE_FIREBASE_APPCHECK_DEBUG_TOKEN). Dev-only —
// import.meta.env.DEV is false in production builds, so this is stripped out.
if (import.meta.env.DEV) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim() || true;
}

export const appCheck: AppCheck | null = appCheckSiteKey
  ? initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    })
  : null;

// Analytics is a non-essential technology. It must only be initialised after
// the user consents to optional cookies (see CookieConsentProvider) — never at
// module load — so we don't drop analytics cookies without consent.
let analyticsInstance: Analytics | null = null;

export const getAnalyticsInstance = (): Analytics | null => analyticsInstance;

export const enableAnalytics = async (): Promise<void> => {
  if (analyticsInstance) return;
  try {
    if (await isSupported()) {
      analyticsInstance = getAnalytics(app);
    }
  } catch (error) {
    console.error('Analytics initialisation failed:', error);
  }
};

// Initialize services
export const auth: Auth = getAuth(app);
export const firestore: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
export const database: Database = getDatabase(app); // Realtime Database for chat
export const cloudFunctions: Functions = getFunctions(app);

// Google Auth Provider
export const googleProvider: GoogleAuthProvider = new GoogleAuthProvider();

export default app;
